<template>
  <v-container fluid class="dashboard-container pa-0">
    <!-- Premium Dashboard Header -->
    <v-sheet
      color="white"
      class="pa-8 mb-8 border-bottom shadow-sm"
    >
      <v-row align="center">
        <v-col cols="12" md="6">
          <div class="d-flex align-center">
            <v-avatar size="64" color="primary lighten-5" class="mr-6 shadow-xs rounded-xl border-white">
              <v-img 
                v-if="parameters.logo_url" 
                :src="parameters.logo_url" 
                contain
                max-width="40"
              ></v-img>
              <v-icon v-else color="primary" large>fas fa-chart-line</v-icon>
            </v-avatar>
            <div>
              <div class="d-flex align-center mb-1">
                <h1 class="text-h4 font-weight-black primary--text tracking-tighter">Panel de Analytics</h1>
                <v-chip x-small color="success" class="ml-4 font-weight-black">LIVE</v-chip>
              </div>
              <p class="body-2 grey--text font-weight-medium mb-0">
                Business intelligence y métricas clave de rendimiento operativo
              </p>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="6" class="d-flex justify-md-end align-center">
          <div class="d-flex align-center bg-lightgrey px-4 py-2 rounded-xl border shadow-xs mr-4">
            <v-icon x-small color="grey" class="mr-2">fas fa-calendar-alt</v-icon>
            <v-select
              v-model="selectedPeriod"
              :items="periodOptions"
              solo
              flat
              dense
              hide-details
              background-color="transparent"
              class="period-selector-modern"
              @change="handlePeriodChange"
            ></v-select>
          </div>
          <v-btn
            color="primary"
            class="rounded-xl px-6 font-weight-black text-none shadow-sm"
            @click="refreshData"
            :loading="loading"
          >
            <v-icon left size="14">fas fa-sync-alt</v-icon>
            Actualizar Data
          </v-btn>
        </v-col>
      </v-row>
    </v-sheet>

    <!-- Modal para Fechas Personalizadas -->
    <v-dialog v-model="showCustomDateDialog" max-width="450" transition="dialog-bottom-transition">
      <v-card class="modern-card overflow-hidden">
        <v-toolbar flat class="bg-lightgrey border-bottom">
          <v-toolbar-title class="body-1 font-weight-black primary--text uppercase tracking-widest">Periodo Personalizado</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="showCustomDateDialog = false"><v-icon small>fas fa-times</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pa-8">
          <v-row>
            <v-col cols="12" sm="6">
              <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Fecha Inicio</div>
              <v-text-field v-model="customStartDate" type="date" outlined dense class="modern-input"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Fecha Fin</div>
              <v-text-field v-model="customEndDate" type="date" outlined dense class="modern-input"></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-6 bg-lightgrey justify-end">
          <v-btn text @click="showCustomDateDialog = false" class="text-none font-weight-bold grey--text px-6">Cancelar</v-btn>
          <v-btn color="primary" @click="applyCustomDate" class="rounded-lg px-8 font-weight-black text-none shadow-sm">Aplicar Filtro</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="px-8 pb-12">
      <!-- Dynamic Metric Cards Grid -->
      <v-row dense>
        <v-col v-if="parameters.dashboard_card_quoted" cols="12" sm="6" md="3" lg="3">
          <v-card class="modern-card-metric border-left-blue shadow-xs h-100">
            <v-card-text class="pa-6">
              <div class="d-flex justify-space-between align-start mb-4">
                <v-avatar color="blue lighten-5" size="44" class="rounded-lg border-blue-light">
                  <v-icon color="blue" size="20">fas fa-file-invoice-dollar</v-icon>
                </v-avatar>
                <div class="text-right">
                  <div class="caption font-weight-black grey--text text-uppercase mb-1">Cotizado</div>
                  <div class="text-h5 font-weight-black primary--text">${{ formatCurrency(metrics.cotizado) }}</div>
                </div>
              </div>
              <div class="d-flex align-center">
                <v-chip x-small color="blue lighten-4" class="blue--text font-weight-black mr-2">+12%</v-chip>
                <span class="caption grey--text font-weight-bold tracking-tighter">vs mes anterior</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col v-if="parameters.dashboard_card_confirmed" cols="12" sm="6" md="3" lg="3">
          <v-card class="modern-card-metric border-left-success shadow-xs h-100">
            <v-card-text class="pa-6">
              <div class="d-flex justify-space-between align-start mb-4">
                <v-avatar color="success lighten-5" size="44" class="rounded-lg border-success-light">
                  <v-icon color="success" size="20">fas fa-check-circle</v-icon>
                </v-avatar>
                <div class="text-right">
                  <div class="caption font-weight-black grey--text text-uppercase mb-1">Confirmado</div>
                  <div class="text-h5 font-weight-black primary--text">${{ formatCurrency(metrics.confirmado) }}</div>
                </div>
              </div>
              <div class="d-flex align-center">
                <v-chip x-small color="success lighten-4" class="success--text font-weight-black mr-2">+8%</v-chip>
                <span class="caption grey--text font-weight-bold tracking-tighter">conversión activa</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col v-if="parameters.dashboard_card_confirmed_finished" cols="12" sm="6" md="3" lg="3">
          <v-card class="modern-card-metric border-left-purple shadow-xs h-100">
            <v-card-text class="pa-6">
              <div class="d-flex justify-space-between align-start mb-4">
                <v-avatar color="purple lighten-5" size="44" class="rounded-lg border-purple-light">
                  <v-icon color="purple" size="20">fas fa-flag-checkered</v-icon>
                </v-avatar>
                <div class="text-right">
                  <div class="caption font-weight-black grey--text text-uppercase mb-1">Finalizado</div>
                  <div class="text-h5 font-weight-black primary--text">${{ formatCurrency(metrics.finalizado) }}</div>
                </div>
              </div>
              <div class="d-flex align-center">
                <v-progress-linear value="100" color="purple" height="4" rounded class="mt-1"></v-progress-linear>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col v-if="parameters.dashboard_card_confirmed_finished_not_invoiced" cols="12" sm="6" md="3" lg="3">
          <v-card class="modern-card-metric border-left-orange shadow-xs h-100">
            <v-card-text class="pa-6">
              <div class="d-flex justify-space-between align-start mb-4">
                <v-avatar color="orange lighten-5" size="44" class="rounded-lg border-orange-light">
                  <v-icon color="orange lighten-1" size="20">fas fa-exclamation-triangle</v-icon>
                </v-avatar>
                <div class="text-right">
                  <div class="caption font-weight-black grey--text text-uppercase mb-1 lh-1-2">Pendiente Facturar</div>
                  <div class="text-h5 font-weight-black primary--text">${{ formatCurrency(metrics.finalizadoSinDte) }}</div>
                </div>
              </div>
              <div class="caption orange--text text--darken-3 font-weight-black uppercase tracking-tighter">Requiere atención DTE</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col v-if="parameters.dashboard_card_invoiced" cols="12" sm="6" md="3" lg="3">
          <v-card class="modern-card-metric border-left-teal shadow-xs h-100">
            <v-card-text class="pa-6">
              <div class="d-flex justify-space-between align-start mb-4">
                <v-avatar color="teal lighten-5" size="44" class="rounded-lg border-teal-light">
                  <v-icon color="teal" size="20">fas fa-receipt</v-icon>
                </v-avatar>
                <div class="text-right">
                  <div class="caption font-weight-black grey--text text-uppercase mb-1">Facturado Neto</div>
                  <div class="text-h5 font-weight-black primary--text">${{ formatCurrency(metrics.facturadoNeto) }}</div>
                </div>
              </div>
              <div class="caption teal--text font-weight-black text-uppercase tracking-tighter">Ingresos Brutos Reconocidos</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col v-if="parameters.dashboard_card_paid" cols="12" sm="6" md="3" lg="3">
          <v-card class="modern-card-metric border-left-success shadow-xs h-100">
            <v-card-text class="pa-6">
              <div class="d-flex justify-space-between align-start mb-4">
                <v-avatar color="success lighten-5" size="44" class="rounded-lg border-success-light">
                  <v-icon color="success" size="20">fas fa-money-bill-wave</v-icon>
                </v-avatar>
                <div class="text-right">
                  <div class="caption font-weight-black grey--text text-uppercase mb-1">Recaudado</div>
                  <div class="text-h5 font-weight-black success--text">${{ formatCurrency(metrics.recaudado) }}</div>
                </div>
              </div>
              <div class="caption grey--text font-weight-medium italic lh-1">Flujo de caja registrado</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col v-if="parameters.dashboard_card_purchase_subtotal" cols="12" sm="6" md="3" lg="3">
          <v-card class="modern-card-metric border-left-red shadow-xs h-100">
            <v-card-text class="pa-6">
              <div class="d-flex justify-space-between align-start mb-4">
                <v-avatar color="red lighten-5" size="44" class="rounded-lg border-red-light">
                  <v-icon color="red" size="20">fas fa-shopping-cart</v-icon>
                </v-avatar>
                <div class="text-right">
                  <div class="caption font-weight-black grey--text text-uppercase mb-1">Compras Neto</div>
                  <div class="text-h5 font-weight-black red--text text--darken-2">${{ formatCurrency(metrics.comprasNeto) }}</div>
                </div>
              </div>
              <div class="caption red--text text--lighten-1 font-weight-black uppercase tracking-tighter">Costos Operativos</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col v-if="parameters.dashboard_card_purchase_paid" cols="12" sm="6" md="3" lg="3">
          <v-card class="modern-card-metric border-left-secondary shadow-xs h-100">
            <v-card-text class="pa-6">
              <div class="d-flex justify-space-between align-start mb-4">
                <v-avatar color="secondary lighten-5" size="44" class="rounded-lg border-secondary-light">
                  <v-icon color="secondary" size="20">fas fa-file-invoice</v-icon>
                </v-avatar>
                <div class="text-right">
                  <div class="caption font-weight-black grey--text text-uppercase mb-1">Compras Pagado</div>
                  <div class="text-h5 font-weight-black primary--text">${{ formatCurrency(metrics.comprasPagado) }}</div>
                </div>
              </div>
              <div class="caption secondary--text font-weight-black uppercase tracking-tighter">Egresos Consolidados</div>
            </v-card-text>
          </v-card>
        </v-col>

      </v-row>

      <!-- Trends Section -->
      <v-row class="mt-8">
        <v-col cols="12" lg="8">
          <v-card class="modern-card shadow-sm h-100">
            <v-toolbar flat class="white pt-2">
              <v-avatar color="primary lighten-5" size="32" class="mr-3">
                <v-icon color="primary" x-small>fas fa-chart-area</v-icon>
              </v-avatar>
              <v-toolbar-title class="body-1 font-weight-black primary--text uppercase tracking-widest">Tendencia de Ventas</v-toolbar-title>
              <v-spacer></v-spacer>
              <div class="d-flex align-center">
                <v-badge dot color="blue" class="mr-6">
                  <span class="caption grey--text font-weight-bold ml-2">Confirmadas</span>
                </v-badge>
                <v-badge dot color="primary" class="mr-2">
                  <span class="caption grey--text font-weight-bold ml-2">Cotizadas</span>
                </v-badge>
              </div>
            </v-toolbar>
            <v-card-text class="pa-6">
              <div style="height: 350px">
                <LineChart :chart-data="chartData" :options="chartOptions" :height="350" />
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" lg="4">
          <v-card class="modern-card shadow-sm h-100 overflow-hidden">
            <v-toolbar flat class="white pt-2 border-bottom">
              <v-avatar color="orange lighten-5" size="32" class="mr-3">
                <v-icon color="orange" x-small>fas fa-award</v-icon>
              </v-avatar>
              <v-toolbar-title class="body-1 font-weight-black primary--text uppercase tracking-widest">Top Performers</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn icon small color="grey"><v-icon x-small>fas fa-ellipsis-v</v-icon></v-btn>
            </v-toolbar>
            
            <v-tabs v-model="topTab" grow color="primary" class="dashboard-top-tabs">
              <v-tab class="text-none font-weight-bold caption">Líneas</v-tab>
              <v-tab class="text-none font-weight-bold caption">Clientes</v-tab>
              <v-tab class="text-none font-weight-bold caption">Vendedores</v-tab>
            </v-tabs>

            <v-tabs-items v-model="topTab">
              <v-tab-item v-for="n in 3" :key="n" class="pa-4 bg-lightgrey" style="min-height: 340px">
                <div v-if="topTab === 0" class="top-list">
                  <div v-for="(item, idx) in topProductsList" :key="idx" class="top-item-card mb-3 pa-4 bg-white rounded-lg shadow-xs d-flex align-center">
                    <v-avatar color="primary lighten-5" size="32" class="mr-4 primary--text font-weight-black body-2">{{ idx + 1 }}</v-avatar>
                    <div class="flex-grow-1">
                      <div class="body-2 font-weight-black primary--text tracking-tighter">{{ item.name }}</div>
                      <div class="caption grey--text">${{ formatCurrency(item.revenue) }}</div>
                      <v-progress-linear :value="item.percentage" color="primary" height="4" rounded class="mt-2"></v-progress-linear>
                    </div>
                    <div class="ml-4 primary--text font-weight-black caption">{{ item.percentage }}%</div>
                  </div>
                </div>

                <div v-if="topTab === 1" class="top-list">
                  <div v-for="(item, idx) in topCustomersList" :key="idx" class="top-item-card mb-3 pa-4 bg-white rounded-lg shadow-xs d-flex align-center">
                    <v-avatar color="secondary lighten-5" size="32" class="mr-4 secondary--text font-weight-black body-2">{{ idx + 1 }}</v-avatar>
                    <div class="flex-grow-1">
                      <div class="body-2 font-weight-black primary--text tracking-tighter">{{ item.name }}</div>
                      <div class="caption grey--text">${{ formatCurrency(item.revenue) }}</div>
                      <v-progress-linear :value="item.percentage" color="secondary" height="4" rounded class="mt-2"></v-progress-linear>
                    </div>
                    <div class="ml-4 secondary--text font-weight-black caption">{{ item.percentage }}%</div>
                  </div>
                </div>

                <div v-if="topTab === 2" class="top-list">
                  <div v-for="(item, idx) in topSellersList" :key="idx" class="top-item-card mb-3 pa-4 bg-white rounded-lg shadow-xs d-flex align-center">
                    <v-avatar color="teal lighten-5" size="32" class="mr-4 teal--text font-weight-black body-2">{{ idx + 1 }}</v-avatar>
                    <div class="flex-grow-1">
                      <div class="body-2 font-weight-black primary--text tracking-tighter">{{ item.name }}</div>
                      <div class="caption grey--text">${{ formatCurrency(item.revenue) }}</div>
                      <v-progress-linear :value="item.percentage" color="teal" height="4" rounded class="mt-2"></v-progress-linear>
                    </div>
                    <div class="ml-4 teal--text font-weight-black caption">{{ item.percentage }}%</div>
                  </div>
                </div>
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
      </v-row>

      <!-- Footer Info -->
      <v-sheet color="transparent" class="mt-8 d-flex align-center">
        <v-icon color="grey" x-small class="mr-2">fas fa-info-circle</v-icon>
        <span class="caption grey--text font-weight-medium">
          Última actualización sincronizada: <span class="primary--text font-weight-bold">{{ lastUpdate }}</span> | Período visualización: <span class="primary--text font-weight-bold">{{ currentPeriod }}</span>
        </span>
      </v-sheet>
    </div>
  </v-container>
</template>

<script>
import LineChart from '@/components/charts/LineChart.vue'
import BarChart from '@/components/charts/BarChart.vue'
const numeral = require('numeral')

export default {
  name: 'Dashboard',
  components: {
    LineChart,
    BarChart
  },
  data: () => ({
    loading: false,
    selectedPeriod: 'current_month',
    topTab: 0,
    periodOptions: [
      { text: 'Este Mes', value: 'current_month' },
      { text: 'Mes Anterior', value: 'last_month' },
      { text: 'Este Trimestre', value: 'current_quarter' },
      { text: 'Este Año', value: 'current_year' },
      { text: 'Personalizado', value: 'custom' }
    ],
    metrics: {
      cotizado: 62048500,
      confirmado: 61071500,
      finalizado: 45030200,
      finalizadoSinDte: 12400500,
      facturadoNeto: 58071500,
      recaudado: 1534000,
      comprasNeto: 12995000,
      comprasPagado: 1870000
    },
    lastUpdate: '',
    currentPeriod: 'Enero 2024',
    showCustomDateDialog: false,
    customStartDate: null,
    customEndDate: null,
    chartData: { labels: [], datasets: [] },
    chartOptions: {},
    topProductsList: [],
    topCustomersList: [],
    topSellersList: []
  }),
  computed: {
    parameters() {
      return this.$store.getters.getCompany || {}
    },
    internacional() {
      return this.$store.getters.getInternational || { tax_id_label: 'RUT', purchase_tax_label: 'IVA' }
    }
  },
  watch: {
    selectedPeriod(newPeriod) {
      this.updatePeriodData(newPeriod)
    }
  },
  methods: {
    formatCurrency(value) {
      return numeral(value).format('0,0')
    },
    refreshData() {
      this.loading = true
      setTimeout(() => {
        this.loading = false
        this.lastUpdate = new Date().toLocaleTimeString('es-CL', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true
        })
      }, 1500)
    },
    handlePeriodChange(newPeriod) {
      if (newPeriod === 'custom') {
        this.showCustomDateDialog = true
        this.customStartDate = null
        this.customEndDate = null
      } else {
        this.updatePeriodData(newPeriod)
      }
    },
    applyCustomDate() {
      if (this.customStartDate && this.customEndDate) {
        this.selectedPeriod = 'custom'
        this.currentPeriod = `${this.customStartDate} al ${this.customEndDate}`
        this.showCustomDateDialog = false
        this.updatePeriodData('custom')
      }
    },
    updatePeriodData(period) {
      const periodLabels = {
        'current_month': 'Enero 2024',
        'last_month': 'Diciembre 2023',
        'current_quarter': 'Q1 2024',
        'current_year': '2024',
        'custom': 'Periodo Personalizado'
      }
      this.currentPeriod = periodLabels[period] || this.currentPeriod
      this.refreshData()
      this.updateChartData(period)
      this.updateTopLists(period)
    },
    updateChartData(period) {
      const labels = []
      const confirmed = []
      const quoted = []
      for (let i = 1; i <= 30; i++) {
        labels.push(`${i} Ene`)
        confirmed.push(Math.floor(Math.random() * 2000000) + 500000)
        quoted.push(Math.floor(Math.random() * 2500000) + 600000)
      }
      this.chartData = {
        labels,
        datasets: [
          {
            label: 'Confirmadas',
            data: confirmed,
            borderColor: '#35BDC8',
            backgroundColor: 'rgba(53, 189, 200, 0.05)',
            borderWidth: 3,
            fill: true,
            tension: 0.4,
            pointRadius: 0
          },
          {
            label: 'Cotizadas',
            data: quoted,
            borderColor: '#1A1F2E',
            backgroundColor: 'transparent',
            borderWidth: 2,
            borderDash: [5, 5],
            fill: false,
            tension: 0.4,
            pointRadius: 0
          }
        ]
      }
      this.chartOptions = this.getChartOptions()
    },
    updateTopLists(period) {
      this.topProductsList = [
        { name: 'Línea Industrial', revenue: 25000000, percentage: 78 },
        { name: 'Línea Comercial', revenue: 22000000, percentage: 65 },
        { name: 'Línea Residencial', revenue: 18000000, percentage: 45 },
        { name: 'Línea Agrícola', revenue: 15000000, percentage: 35 },
        { name: 'Línea Automotriz', revenue: 8000000, percentage: 20 }
      ]
      this.topCustomersList = [
        { name: 'Constructora S.A.', revenue: 45000000, percentage: 85 },
        { name: 'Logística Global', revenue: 38000000, percentage: 72 },
        { name: 'Inversiones del Norte', revenue: 31000000, percentage: 60 },
        { name: 'Retail Express', revenue: 25000000, percentage: 48 },
        { name: 'Manufacturas SpA', revenue: 19500000, percentage: 35 }
      ]
      this.topSellersList = [
        { name: 'Andrés Vicuña', revenue: 52000000, percentage: 92 },
        { name: 'Camila Soto', revenue: 48000000, percentage: 88 },
        { name: 'Rodrigo Jara', revenue: 41000000, percentage: 75 },
        { name: 'Elena Martínez', revenue: 35000000, percentage: 62 },
        { name: 'Juan Pérez', revenue: 28000000, percentage: 45 }
      ]
    },
    getChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          mode: 'index',
          intersect: false,
          backgroundColor: '#fff',
          titleFontColor: '#1A1F2E',
          bodyFontColor: '#607d8b',
          borderColor: '#eceff1',
          borderWidth: 1,
          cornerRadius: 8,
          xPadding: 16,
          yPadding: 16,
          callbacks: {
            label: (item, data) => `${data.datasets[item.datasetIndex].label}: $${numeral(item.yLabel).format('0,0')}`
          }
        },
        scales: {
          xAxes: [{ gridLines: { display: false }, ticks: { fontColor: '#9aa0a6', fontSize: 11, padding: 10 } }],
          yAxes: [{ 
            gridLines: { color: '#f1f3f4', borderDash: [4, 4], drawBorder: false }, 
            ticks: { fontColor: '#9aa0a6', fontSize: 11, padding: 10, callback: (v) => '$' + numeral(v).format('0.0a') }
          }]
        }
      }
    }
  },
  mounted() {
    this.refreshData()
    this.$nextTick(() => {
      this.updateChartData(this.selectedPeriod)
      this.updateTopLists(this.selectedPeriod)
    })
  }
}
</script>

<style scoped>
.dashboard-container { background-color: #f4f7f9; min-height: 100vh; font-family: 'Outfit', sans-serif !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.shadow-xs { box-shadow: 0 1px 4px rgba(0,0,0,0.03) !important; }
.rounded-xl { border-radius: 12px !important; }

.bg-lightgrey { background-color: #f8fbfc !important; }
.tracking-widest { letter-spacing: 1px; }
.tracking-tighter { letter-spacing: -0.5px; }
.uppercase { text-transform: uppercase; }

.period-selector-modern { width: 140px; font-weight: 700; font-size: 0.85rem; color: #455a64 !important; }
.period-selector-modern >>> .v-select__selections { color: #1e88e5; }
.period-selector-modern >>> .v-input__slot { border: none !important; }

.modern-card { border-radius: 16px !important; border: 1px solid rgba(0,0,0,0.04) !important; }
.modern-card-metric { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.04) !important; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
.modern-card-metric:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.08) !important; }

.border-left-blue { border-left: 4px solid #35BDC8 !important; }
.border-left-success { border-left: 4px solid #4caf50 !important; }
.border-left-purple { border-left: 4px solid #9c27b0 !important; }
.border-left-orange { border-left: 4px solid #ff9800 !important; }
.border-left-teal { border-left: 4px solid #009688 !important; }
.border-left-red { border-left: 4px solid #f44336 !important; }
.border-left-secondary { border-left: 4px solid #607d8b !important; }

.modern-input >>> .v-input__control >>> .v-input__slot { background-color: #f8fbfc !important; border-radius: 8px !important; }

.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.2 !important; }
.italic { font-style: italic; }

.dashboard-top-tabs { border-bottom: 1px solid rgba(0,0,0,0.05); }
.dashboard-top-tabs >>> .v-tabs-bar { height: 48px; }
.top-item-card { transition: all 0.2s ease; border: 1px solid transparent; }
.top-item-card:hover { border-color: rgba(30, 136, 229, 0.2); transform: scale(1.02); }

.opacity-60 { opacity: 0.6; }
.text-none { text-transform: none !important; }
</style>